import axios, { CancelTokenSource } from 'axios';
import { useCallback, useRef } from 'react';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import { InvoiceBudgetPeriodResponse } from 'src/sections/invoice_details/InvoiceDetailsContext';
import { round } from 'src/utils';
import axiosInstance from 'src/utils/axios';
import { InvoiceDetailsResponse } from './useInvoiceDetailsApi';
import { InvoiceGlBreakoutItem } from './useInvoiceGlBreakoutApi';
import { InvoiceLineItem } from './useInvoiceLineItemsApi';

interface UpdateInvoiceRequest extends InvoiceDetailsResponse {
  lineItems: InvoiceLineItem[];
  invoiceGlBreakouts: InvoiceGlBreakoutItem[];
  externalSupplier: string;
  periodAllocations: InvoiceBudgetPeriodResponse[];
  accrualDate: string | null;
}

export interface UpdateInvoiceResponse extends InvoiceDetailsResponse {}

const useUpdateInvoiceApi = (invoiceId: string) => {
  const reqRef = useRef<CancelTokenSource>();
  const call = useCallback(
    async (data?: UpdateInvoiceRequest) => {
      if (!data) return {} as UpdateInvoiceResponse;
      const requestData: UpdateInvoiceRequest = {
        ...data,
        discountAmt: data?.discountAmt ?? 0,
        invoiceGlBreakouts: data.invoiceGlBreakouts.map((glBreakoutItem) => ({
          ...glBreakoutItem,
          discountAmt: glBreakoutItem.discountAmt * -1,
        })),
        lineItems: data.lineItems.map((lineItem) => ({
          ...lineItem,
          receiptLineItemId: lineItem?.receiptLineItemId
            ? lineItem?.receiptLineItemId?.toString()?.endsWith('987654321')
              ? (undefined as unknown as number)
              : lineItem.receiptLineItemId
            : (undefined as unknown as number),
        })),
      };
      const req = axios.CancelToken.source();
      if (reqRef.current) {
        reqRef.current.cancel();
      }
      reqRef.current = req;

      const payload = {
        invoiceNumber: requestData.invoiceNumber,
        invoiceDate: requestData.invoiceDate,
        receivedDate: requestData.receivedDate,
        discountAmt: round(Number(requestData.discountAmt || 0), 2),
        shipCostAmt: round(Number(requestData.shipCostAmt || 0), 2),
        totalCostAmt: requestData.totalCostAmt,
        salesTaxAmt: round(Number(requestData.salesTaxAmt || 0), 2),
        notes: requestData.notes,
        prepaid: requestData.prepaid,
        isAutoDistGlBreakout: requestData.isAutoDistGlBreakout,
        apId: requestData.apId,
        externalSupplier: requestData.externalSupplier,
        accrualDate: requestData.accrualDate,
        lineItems: requestData.lineItems.map((el) => ({
          receiptLineItemId: el.receiptLineItemId,
          quantityReceived: el.quantityReceived,
          priceAmt: el.priceAmt,
          isTaxable: el.isTaxable,
          glCd: el.glCd,
          uom: el.uom,
          description: el.description,
          productItemNum: el.productItemNum,
        })),
        invoiceGlBreakouts: requestData.invoiceGlBreakouts.map((out) => ({
          glCodeId: out.glCodeId,
          glCode: out.glCode,
          salesTaxAmt: round(Number(out.salesTaxAmt || 0), 2),
          shipCostAmt: round(Number(out.shipCostAmt || 0), 2),
          discountAmt: round(Number(out.discountAmt || 0), 2),
          notes: out.notes,
        })),
      };
      const res = await axiosInstance.put<UpdateInvoiceResponse>(
        api.updateInvoice(invoiceId),
        payload,
        { cancelToken: req.token }
      );
      return res.data;
    },
    [invoiceId]
  );

  const { execute, isLoading, status } = useAsync<UpdateInvoiceResponse, UpdateInvoiceRequest>(
    call,
    false,
    undefined,
    {} as UpdateInvoiceResponse
  );

  return { updateInvoice: execute, isUpdatingInvoice: isLoading, status, reqRef };
};

export default useUpdateInvoiceApi;
